/**
 * web-api 登录 v3（/v3/auth/*）的本地适配。
 *
 * 接口调用直接用生成的 services/api/authV3Controller，这里只做几件事：
 *   1. 自动带上 productType、companyType（v3 必填，可按次传入覆盖）；
 *   2. 刷新 token、冷启动的返回转回旧结构：data 仍是 { value, expire }，调用方读 res.data.value 不用改；
 *   3. 取授权链接默认带 path='/'、params='{}'（后端回调对空值 decode 会报错，回到原页面全靠 path）。
 * 返回统一是接口原样的 { code, msg, data }，业务错误码原样透传。
 * 登录 / 注册的 data 是 v3 的 AuthTokenResp：{ accessToken: {value, expire}, refreshToken, userInfo, wxInfo, isNewUser, needBindMobile, deviceNumber }，
 * 是否需要去注册看 needBindMobile，不要再看有没有 refreshToken。
 */
import {
  v3AuthTokenRefreshUsingPOST,
  v3AuthWxAuthorizeUrlUsingGET,
  v3AuthLoginSessionUsingPOST,
  v3AuthLoginWxPcUsingPOST,
  v3AuthLoginMobileUsingPOST,
  v3AuthRegisterWxOaUsingPOST,
  v3AuthCodeSmsUsingPOST,
  v3AuthCodeVoiceUsingPOST,
  v3AuthMobileChangeUsingPOST,
  v3AuthMobileChangeQuotaUsingGET,
  v3AuthWxUnbindUsingPOST,
  v3AuthSessionCheckUsingGET,
} from '@/services/api/authV3Controller';
import commonSetting from '@/config/commonSetting';

/** 账号已在其他设备登录（login/session 的 App 互踢） */
export const LOGIN_KICKED = 81000023;

const productTypeOf = (input) => (input && input.productType != null ? input.productType : commonSetting.PRODUCT_TYPE);
const companyTypeOf = (input) => {
  if (input && input.companyType != null) return input.companyType;
  return commonSetting.COMPANY_TYPE != null ? commonSetting.COMPANY_TYPE : 45;
};

// 业务失败时原样返回；request 层返回空（网络异常被吞）时给一个失败码，避免调用方读 res.code 报错
const mapData = (res, fn) => {
  if (!res || typeof res !== 'object') return { code: -1, msg: '请求失败' };
  if (res.code !== 0) return res;
  return { ...res, data: fn(res.data) };
};

/** 刷新 accessToken：{ accessToken, refreshToken, deviceInfo? }，data 为 { value, expire } */
const refreshToken = async (input) => {
  const res = await v3AuthTokenRefreshUsingPOST({
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    productType: productTypeOf(input),
    deviceInfo: input.deviceInfo,
  });
  return mapData(res, (d) => (d && d.accessToken) || null);
};

/** 取公众号授权链接：{ path, params, scope, state, redirectUri, productType? }；传 redirectUri 时忽略 path / params */
const getAuthorizeUrl = (input = {}) => {
  const category = input.category || 'product';
  if (input.redirectUri) {
    return v3AuthWxAuthorizeUrlUsingGET({
      companyType: companyTypeOf(input),
      category,
      redirectUri: input.redirectUri,
      scope: input.scope,
      state: input.state,
    });
  }
  const params = input.params == null ? '{}' : typeof input.params === 'string' ? input.params : JSON.stringify(input.params);
  return v3AuthWxAuthorizeUrlUsingGET({
    companyType: companyTypeOf(input),
    productType: productTypeOf(input),
    category,
    path: input.path || '/',
    params,
    scope: input.scope,
    state: input.state,
  });
};

/**
 * 冷启动恢复会话：{ accessToken, refreshToken, deviceInfo? }。
 * data 为 { value, expire }（同旧 /app/auth/session-login）；App 被踢时 code 为 LOGIN_KICKED，data 为 { lastLoginTime }
 */
const sessionLogin = async (input) => {
  const res = await v3AuthLoginSessionUsingPOST({
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    productType: productTypeOf(input),
    deviceInfo: input.deviceInfo,
  });
  return mapData(res, (d) => (d && d.accessToken) || null);
};

/** PC 扫码登录：{ code, state, accountType, deviceInfo: { platform: 'pc', deviceNumber, appVersion } } */
const loginWxPc = (input) =>
  v3AuthLoginWxPcUsingPOST({
    code: input.code,
    state: input.state,
    accountType: input.accountType,
    productType: productTypeOf(input),
    companyType: companyTypeOf(input),
    deviceInfo: input.deviceInfo,
  });

/** 手机号 + 验证码登录：{ encryptData（SM2 {mobile, validateNo}）, source, deviceInfo?, captchaVerifyParam?, remember? } */
const loginMobile = (input) =>
  v3AuthLoginMobileUsingPOST({
    encryptData: input.encryptData,
    source: input.source,
    productType: productTypeOf(input),
    companyType: companyTypeOf(input),
    deviceInfo: input.deviceInfo,
    captchaVerifyParam: input.captchaVerifyParam,
    remember: input.remember,
  });

/**
 * 已微信授权（持游客 token）的用户补手机号注册，不传 code，服务端取当前登录态里的 wxId。
 * 短信：{ mobileSource: 'sms', encryptData, source }；H5 一键：{ mobileSource: 'one-key', token, userInformation, source }
 */
const registerByCurrentWx = (input) => {
  const body = {
    mobileSource: input.mobileSource,
    productType: productTypeOf(input),
    companyType: companyTypeOf(input),
    source: input.source,
    deviceInfo: input.deviceInfo,
  };
  if (input.mobileSource === 'one-key') {
    body.token = input.token;
    body.platform = 'h5';
    body.userInformation = input.userInformation;
  } else {
    body.encryptData = input.encryptData;
  }
  return v3AuthRegisterWxOaUsingPOST(body);
};

const sendCodeBody = (input) => ({
  encryptData: input.encryptData,
  captchaVerifyParam: input.captchaVerifyParam,
  scene: input.scene,
  companyType: companyTypeOf(input),
  productType: productTypeOf(input),
});

/** 发短信验证码：{ encryptData（SM2 {mobile}）, captchaVerifyParam（必传）, scene? } */
const sendSmsCode = (input) => v3AuthCodeSmsUsingPOST(sendCodeBody(input));

/** 发语音验证码：同 sendSmsCode */
const sendVoiceCode = (input) => v3AuthCodeVoiceUsingPOST(sendCodeBody(input));

/** 换绑手机号：{ encryptData（SM2 {mobile, validateNo}）, captchaVerifyParam? } */
const changeMobile = (input) =>
  v3AuthMobileChangeUsingPOST({
    encryptData: input.encryptData,
    captchaVerifyParam: input.captchaVerifyParam,
    companyType: companyTypeOf(input),
    productType: productTypeOf(input),
  });

/** 换绑手机号额度检查：超限时 code 非 0 */
const changeMobileQuota = () => v3AuthMobileChangeQuotaUsingGET();

/** 解绑微信：成功后服务端重新签发 token（data 为 AuthTokenResp），调用方要写回 */
const unbindWx = (input = {}) =>
  v3AuthWxUnbindUsingPOST({
    productType: productTypeOf(input),
    accountType: input.accountType,
  });

/** PC 设备占用（互踢）检查：{ deviceNumber }，data 为 { isLogin, lastLoginTime } */
const checkPcSession = (input) => v3AuthSessionCheckUsingGET({ deviceNumber: input.deviceNumber });

export default {
  refreshToken,
  getAuthorizeUrl,
  sessionLogin,
  loginWxPc,
  loginMobile,
  registerByCurrentWx,
  sendSmsCode,
  sendVoiceCode,
  changeMobile,
  changeMobileQuota,
  unbindWx,
  checkPcSession,
};
